import React, { useEffect, useState, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Input from '@material-ui/core/Input';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    gap: theme.spacing(2), // Reduced gap for mobile
    width: '100%',
    marginBottom: theme.spacing(2),
    justifyContent: 'center',
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1), // Even smaller gap on mobile
    },
  },
  containerFullWidth: {
    display: 'flex',
    gap: theme.spacing(2), // Reduced gap for mobile
    width: '100%',
    marginBottom: theme.spacing(2),
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1), // Even smaller gap on mobile
    },
  },
  codeInput: {
    width: theme.spacing(5),
    fontSize: '3rem',
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(4), // Smaller width on mobile
      fontSize: '2.5rem', // Smaller font on mobile
    },
    [theme.breakpoints.down('xs')]: {
      width: theme.spacing(3.5), // Even smaller on very small screens
      fontSize: '2rem', // Even smaller font
    },
    '& input': {
      textAlign: 'center',
      fontSize: '3rem',
      padding: theme.spacing(0.5), // Add some padding
      [theme.breakpoints.down('sm')]: {
        fontSize: '2.5rem',
        padding: theme.spacing(0.25),
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: '2rem',
        padding: theme.spacing(0.25),
      },
    },
  },
  codeInputFullWidth: {
    flex: 1,
    minWidth: 0,
    fontSize: '3rem',
    '& input': {
      textAlign: 'center',
      fontSize: '3rem',
      padding: 0,
    },
  },
  underline: {
    '&:before': {
      borderBottom: '2px solid #4B4B4B',
    },
    '&:hover:before': {
      borderBottom: '2px solid #4B4B4B',
    },
    '&:after': {
      borderBottom: '2px solid #4B4B4B',
    },
  },
  underlineError: {
    '&:before': {
      borderBottom: '2px solid #E74C3C',
    },
    '&:hover:before': {
      borderBottom: '2px solid #E74C3C',
    },
    '&:after': {
      borderBottom: '2px solid #E74C3C',
    },
  },
}));

const CodeInput = ({
  onChange,
  onInputComplete,
  fullWidth = false,
  error = false,
}) => {
  const [verificationCode, setVerificationCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const classes = useStyles();
  const containerRef = useRef(null);

  useEffect(() => {
    onChange(verificationCode.join(''));
  }, [verificationCode, onChange]);

  // Global paste handler as a fallback
  useEffect(() => {
    const handleGlobalPaste = e => {
      // Check if the paste is happening in our component
      const container = containerRef.current;
      if (
        container &&
        (container.contains(e.target) || document.activeElement === container)
      ) {
        handlePaste(e);
      }
    };

    // Also listen for Cmd+V keydown events on Mac
    const handleKeyDown = e => {
      // Check for Cmd+V on Mac or Ctrl+V on Windows/Linux
      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        const container = containerRef.current;
        if (container && container.contains(e.target)) {
          // Let the paste event handle it, but ensure our handler is ready
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePaste]);

  // Add paste event listener to the container and all inputs
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handlePasteEvent = e => {
        handlePaste(e);
      };

      // Add to container
      container.addEventListener('paste', handlePasteEvent);

      // Add to all input elements with a small delay to ensure they're rendered
      const addInputListeners = () => {
        const inputs = container.querySelectorAll('input');
        inputs.forEach(input => {
          input.addEventListener('paste', handlePasteEvent);
        });
      };

      // Try immediately and also after a short delay
      addInputListeners();
      const timeoutId = setTimeout(addInputListeners, 100);

      return () => {
        container.removeEventListener('paste', handlePasteEvent);
        clearTimeout(timeoutId);
        const inputs = container.querySelectorAll('input');
        inputs.forEach(input => {
          input.removeEventListener('paste', handlePasteEvent);
        });
      };
    }
    return undefined;
  }, [handlePaste]);

  const handlePaste = useCallback(
    e => {
      // Get the clipboard data first before preventing default
      let pastedData = '';
      try {
        pastedData = e.clipboardData
          .getData('text')
          .replace(/\D/g, '') // Remove all non-digit characters
          .slice(0, 6); // Take only first 6 digits
      } catch (clipboardError) {
        return;
      }

      if (pastedData && pastedData.length > 0) {
        // Only prevent default if we have valid data to process
        e.preventDefault();

        const newCode = [...verificationCode];
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < 6; i++) {
          newCode[i] = i < pastedData.length ? pastedData[i] : '';
        }
        setVerificationCode(newCode);

        // Focus the next empty input or the last input if all filled
        const nextEmptyIndex = newCode.findIndex(val => !val);
        const inputToFocus = document.querySelector(
          `input[name=code-${nextEmptyIndex > -1 ? nextEmptyIndex : 5}]`,
        );
        if (inputToFocus) inputToFocus.focus();

        // If we have a complete code, trigger verification
        if (pastedData.length === 6 && onInputComplete) {
          onInputComplete(pastedData);
        }
      }
    },
    [verificationCode, onInputComplete],
  );

  // Handle paste on the container as well for better UX
  const handleContainerPaste = e => {
    handlePaste(e);
  };

  const handleChange = (index, value) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length > 1) return; // Prevent multi-character input
    if (numericValue && !/^\d$/.test(numericValue)) return; // Only allow single digits

    const newCode = [...verificationCode];
    newCode[index] = numericValue;
    setVerificationCode(newCode);

    if (numericValue && index < 5) {
      const nextInput = document.querySelector(`input[name=code-${index + 1}]`);
      if (nextInput) nextInput.focus();
    }

    // If all fields are filled, trigger verification
    if (newCode.every(val => val) && newCode.length === 6 && onInputComplete) {
      onInputComplete(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Allow navigation keys, backspace, delete, and numeric keys only
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
    ];

    // Allow paste shortcuts (Cmd+V on Mac, Ctrl+V on Windows/Linux)
    if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
      return; // Don't prevent default, let paste event handle it
    }

    // Prevent non-numeric input
    if (!allowedKeys.includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.querySelector(`input[name=code-${index - 1}]`);
      if (prevInput) prevInput.focus();
    }
  };

  return (
    <Box
      ref={containerRef}
      className={fullWidth ? classes.containerFullWidth : classes.container}
      onPaste={handleContainerPaste}
    >
      {verificationCode.map((digit, index) => (
        <Input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          name={`code-${index}`}
          className={fullWidth ? classes.codeInputFullWidth : classes.codeInput}
          value={digit}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            autoComplete: 'off',
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          autoFocus={index === 0}
          disableUnderline={false}
          classes={{
            underline: error ? classes.underlineError : classes.underline,
          }}
        />
      ))}
    </Box>
  );
};

export default CodeInput;
